import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  HelpCircle, 
  Search, 
  BookOpen, 
  MessageCircle, 
  Phone, 
  Mail,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Info,
  Clock,
  User,
  Tag
} from 'lucide-react'

const HelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [faqs, setFaqs] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  // 从API获取FAQ数据
  useEffect(() => {
    const fetchFaqs = async () => {
      setLoading(true)
      try {
        // 尝试从API获取数据
        const response = await fetch(`/api/faq?category=${selectedCategory}&search=${searchQuery}`)
        if (response.ok) {
          const data = await response.json()
          setFaqs(data.data || [])
        } else {
          // 如果API失败，使用模拟数据
          const mockFaqs = [
          {
            id: 1,
            category: 'getting-started',
            question: '如何开始使用CoreTrust手机？',
            answer: 'CoreTrust手机开箱即用，首次开机后按照引导完成设置即可。主要步骤包括：1）连接网络 2）创建钱包 3）设置生物识别 4）下载应用',
            helpful: 15,
            views: 234,
            lastUpdated: '2024-01-15',
            tags: ['新手', '设置', '钱包']
          },
          {
            id: 2,
            category: 'products',
            question: 'CoreTrust手机支持哪些区块链网络？',
            answer: 'CoreTrust手机支持主流的区块链网络，包括：Ethereum、BSC、Polygon、Arbitrum、Optimism等。我们持续添加更多网络支持。',
            helpful: 23,
            views: 456,
            lastUpdated: '2024-01-14',
            tags: ['区块链', '网络', '支持']
          },
          {
            id: 3,
            category: 'technical',
            question: '如何备份我的钱包？',
            answer: 'CoreTrust手机提供多种备份方式：1）助记词备份（12/24位）2）硬件备份 3）云备份（加密存储）。建议使用助记词备份作为主要方式。',
            helpful: 18,
            views: 189,
            lastUpdated: '2024-01-13',
            tags: ['备份', '安全', '钱包']
          },
          {
            id: 4,
            category: 'products',
            question: '熄屏广告功能如何工作？',
            answer: '熄屏广告在手机锁屏时显示精选内容，用户可以通过滑动、点击等方式与广告互动。所有广告都经过严格筛选，确保内容质量。',
            helpful: 12,
            views: 167,
            lastUpdated: '2024-01-12',
            tags: ['广告', '功能', '熄屏']
          },
          {
            id: 5,
            category: 'technical',
            question: '如何更新CoreTrust手机系统？',
            answer: '系统更新会通过OTA方式推送，您可以在设置-系统-系统更新中检查更新。建议在WiFi环境下进行更新，确保更新过程稳定。',
            helpful: 20,
            views: 298,
            lastUpdated: '2024-01-11',
            tags: ['更新', '系统', 'OTA']
          },
          {
            id: 6,
            category: 'account',
            question: '如何重置DeepLink账户密码？',
            answer: '您可以通过以下方式重置密码：1）在登录页面点击"忘记密码" 2）输入注册邮箱 3）查收重置邮件 4）按提示设置新密码',
            helpful: 8,
            views: 89,
            lastUpdated: '2024-01-10',
            tags: ['账户', '密码', '重置']
          },
          {
            id: 7,
            category: 'getting-started',
            question: 'CoreTrust手机有哪些核心功能？',
            answer: 'CoreTrust手机包含六大核心功能：1）熄屏广告系统 2）空投助手 3）数字银行 4）硬件级钱包 5）应用市场 6）去中心化网络通话',
            helpful: 25,
            views: 345,
            lastUpdated: '2024-01-09',
            tags: ['功能', '介绍', '核心']
          },
          {
            id: 8,
            category: 'products',
            question: '如何设置空投助手？',
            answer: '空投助手可以帮助您自动发现和参与空投活动。设置步骤：1）打开空投助手应用 2）连接您的钱包 3）选择感兴趣的项目 4）开启自动监控',
            helpful: 16,
            views: 278,
            lastUpdated: '2024-01-08',
            tags: ['空投', '助手', '设置']
          },
          {
            id: 9,
            category: 'technical',
            question: 'CoreTrust手机的安全性如何？',
            answer: 'CoreTrust手机采用多层安全防护：1）硬件级安全芯片 2）生物识别认证 3）加密存储 4）安全启动 5）定期安全更新',
            helpful: 22,
            views: 412,
            lastUpdated: '2024-01-07',
            tags: ['安全', '防护', '加密']
          },
          {
            id: 10,
            category: 'account',
            question: '如何联系客服？',
            answer: '您可以通过以下方式联系我们的客服团队：1）在线客服（7x24小时）2）电话支持（工作日9:00-18:00）3）邮件支持（24小时内回复）',
            helpful: 14,
            views: 156,
            lastUpdated: '2024-01-06',
            tags: ['客服', '联系', '支持']
          }
        ]
        
        setFaqs(mockFaqs)
        }
      } catch (error) {
        console.error('获取FAQ数据失败:', error)
        setFaqs([])
      }
      setLoading(false)
    }

    fetchFaqs()
  }, [searchQuery, selectedCategory])

  // 动态计算分类统计
  const categories = [
    { 
      id: 'all', 
      name: '全部', 
      count: faqs.length,
      icon: BookOpen
    },
    { 
      id: 'getting-started', 
      name: '快速开始', 
      count: faqs.filter(faq => faq.category === 'getting-started').length,
      icon: HelpCircle
    },
    { 
      id: 'products', 
      name: '产品使用', 
      count: faqs.filter(faq => faq.category === 'products').length,
      icon: CheckCircle
    },
    { 
      id: 'technical', 
      name: '技术问题', 
      count: faqs.filter(faq => faq.category === 'technical').length,
      icon: AlertCircle
    },
    { 
      id: 'account', 
      name: '账户管理', 
      count: faqs.filter(faq => faq.category === 'account').length,
      icon: User
    }
  ]

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleFaqClick = (faqId) => {
    navigate(`/help/${faqId}`)
  }

  const contactMethods = [
    {
      icon: MessageCircle,
      title: '在线客服',
      description: '7x24小时在线支持',
      action: '开始对话',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Phone,
      title: '电话支持',
      description: '工作日 9:00-18:00',
      action: '+852 1234-5678',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Mail,
      title: '邮件支持',
      description: '24小时内回复',
      action: 'contact@deeplink.hk',
      color: 'from-purple-500 to-pink-500'
    }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>帮助中心 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited帮助中心，提供产品使用指南、技术支持和常见问题解答。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <HelpCircle className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                帮助中心
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
                找到您需要的答案，或联系我们的支持团队
              </p>
              
              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="搜索帮助文档..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-2xl border-0 text-lg focus:ring-2 focus:ring-white/50 focus:outline-none"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-12 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => {
                const Icon = category.icon
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center px-6 py-3 rounded-full transition-all duration-200 ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <span className="font-medium">{category.name}</span>
                    <span className="ml-2 px-2 py-1 bg-white/20 dark:bg-gray-600 rounded-full text-xs">
                      {category.count}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                常见问题
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                找到 {filteredFaqs.length} 个相关问题
              </p>
            </div>

            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => handleFaqClick(faq.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        {faq.question}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                        {faq.answer}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          <span>{faq.helpful} 人觉得有用</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{faq.views} 次查看</span>
                        </div>
                        <div className="flex items-center">
                          <span>更新于 {faq.lastUpdated}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {faq.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 ml-4" />
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  没有找到相关问题
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  尝试使用不同的关键词搜索，或联系我们的支持团队
                </p>
                <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  联系支持
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                需要更多帮助？
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                我们的支持团队随时为您服务
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => {
                const Icon = method.icon
                return (
                  <motion.div
                    key={method.title}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-8 text-center hover:shadow-xl transition-shadow"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${method.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {method.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {method.description}
                    </p>
                    <button className="w-full px-6 py-3 bg-gray-900 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-500 transition-colors">
                      {method.action}
                    </button>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default HelpCenter